import {AfterViewInit, Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TeamsService} from '../../services/teams/teams.service';
import {IntroDialogComponent} from '../modal/introdialog/introdialog.component';
import {MatDialog} from '@angular/material/dialog';
import {SessionService} from '../../services/session/session.service';
import {ApiService} from '../../services/api/api.service';
import {AppSettings} from '../../app.settings';
import {UtilsService} from '../../services/utils/utils.service';
import {StripeService} from '../../services/stripe/stripe.service';
import {UiAlertService} from '../../services/ui-alert/ui-alert.service';
import {Dictionary} from 'highcharts';
import YouTubePlayer from 'youtube-player';
import {ManagedFeatureFlagsService} from '../../services/managed-feature-flags/managed-feature-flags.service';
import {ADMIN_SHARED_SHELL_FLAG} from '../../services/managed-feature-flags/managed-feature-flag-keys';
import {UsersService} from '../../services/users/users.service';
import {Subscription} from 'rxjs';
import {ActiveEmployerCompanyService} from '../../services/active-employer-company/active-employer-company.service';
import {NativeTeamContextService} from '../../services/native-team-context/native-team-context.service';
import {LocalStorageService} from '../../services/localstorage/localstorage.service';
import {TeamManagerWorkspaceService} from '../../services/team-manager-workspace/team-manager-workspace.service';
import {CompanyOffersAccessService} from '../../services/company-offers/company-offers-access.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly INTRO_ACKNOWLEDGED_STORAGE_KEY_PREFIX = 'team-admin-intro-acknowledged:v1';

  public currentWellspace: any;
  private firstTime = true;
  public teams: any[] = [];
  private isIntegrationAdmin = AppSettings.isIntegration();
  public isContentAdmin = AppSettings.isContentAdmin();
  public isCustomerSupport = AppSettings.isCustomerSupport();
  public isBrokerAdmin = AppSettings.isBroker();
  public isFinanceAdmin = AppSettings.isFinance();
  public isPayrollAdmin = AppSettings.isPayrollAdmin();
  public isTeamAdminOrUnmatched = !this.isIntegrationAdmin
    && !this.isCustomerSupport
    && !this.isBrokerAdmin
    && !this.isContentAdmin
    && !this.isPayrollAdmin;
  public showPendingAchMsg = false;
  private player: any;
  public videoUrl = '';
  public posterUrl = '';
  public selectedWellspaceId = '';
  public showHomeWellspaceSelector = false;
  public isNativeTeamContextActive = false;
  public teamAdminWellspaceCreationEnabled = false;
  public homeTeamContextReady = false;
  private homeSelectorEvaluationVersion = 0;
  private meSubscription?: Subscription;

  constructor(
    private router: Router,
    private teamsService: TeamsService,
    private dialog: MatDialog,
    private sessionService: SessionService,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private utilsService: UtilsService,
    private stipeService: StripeService,
    private uiAlertService: UiAlertService,
    private managedFeatureFlagsService: ManagedFeatureFlagsService,
    private activeEmployerCompanyService: ActiveEmployerCompanyService,
    private nativeTeamContextService: NativeTeamContextService,
    private usersService: UsersService,
    private localStorageService: LocalStorageService,
    public teamManagerWorkspace: TeamManagerWorkspaceService,
    public companyOffersAccess: CompanyOffersAccessService,
  ) { }

  ngOnInit(): void {
    void this.getUserTeams();
    this.teamsService.wellspaceChangedObservable.subscribe((wellspace: any): void => {
      this.currentWellspace = wellspace;
      this.selectedWellspaceId = wellspace?.id || '';
    });
    this.meSubscription = this.usersService.meSubscribe((me: any): void => {
      if (me && this.apiService.isAuthenticated()) {
        void this.refreshHomeContext();
      }
    });
    this.apiService.monitorLoginState(loggedIn => {
      if (loggedIn && this.isTeamAdminOrUnmatched) {
        void this.refreshHomeContext();
      } else {
        this.resetHomeWellspaceSelector();
        this.isNativeTeamContextActive = false;
        this.homeTeamContextReady = false;
      }
    });
    this.route.params.subscribe(params => {
      if (params.teamId) {
        void this.teamsService.setWellspace({ id: params.teamId }).then(team => {
          if (team) {
            void this.router.navigate(
              ['billing/stripepaymentmethods'],
              { queryParams: { teamName: team.name } },
            );
          } else {
            void this.router.navigate(['/']);
          }
        });
      }
    });
    this.showPendingAchMessage();
  }

  ngOnDestroy(): void {
    this.meSubscription?.unsubscribe();
  }

  private resetHomeWellspaceSelector(): void {
    this.homeSelectorEvaluationVersion += 1;
    this.showHomeWellspaceSelector = false;
  }

  private async refreshHomeContext(): Promise<void> {
    await this.refreshHomeWellspaceSelector();
    await this.getUserTeams();
    if (
      !this.isNativeTeamContextActive
      && !AppSettings.isBroker()
      && this.teams.length === 0
    ) {
      this.showIntro();
    }
  }

  private async refreshHomeWellspaceSelector(): Promise<void> {
    this.resetHomeWellspaceSelector();
    const evaluationVersion = this.homeSelectorEvaluationVersion;
    const userId = this.sessionService.getUserId();
    if (!this.isTeamAdminOrUnmatched || !userId || !this.apiService.isAuthenticated()) {
      return;
    }

    const me = this.usersService.me();
    const partnerId = `${me?.user?.partnerId || me?.brand?.partnerId || ''}`.trim() || undefined;
    const [sharedAdminShellEnabled, companyState] = await Promise.all([
      this.managedFeatureFlagsService.isEnabled(
        ADMIN_SHARED_SHELL_FLAG,
        'team-admin',
        {userId, partnerId},
      ).catch(() => false),
      this.activeEmployerCompanyService.initialize()
        .catch(() => undefined),
    ]);

    if (
      evaluationVersion !== this.homeSelectorEvaluationVersion
      || !this.apiService.isAuthenticated()
      || this.sessionService.getUserId() !== userId
    ) {
      return;
    }

    this.isNativeTeamContextActive = !!companyState
      && this.activeEmployerCompanyService.isSupportedSingleCompanyWorkspaceActive(companyState);
    this.showHomeWellspaceSelector =
      sharedAdminShellEnabled || this.isNativeTeamContextActive;
  }

  ngAfterViewInit(): void {
    const videoId = UiAlertService.content.HOME_PAGE;
    if (videoId.startsWith('https:')) {
      this.videoUrl = videoId;
      this.posterUrl = UiAlertService.content.HOME_PAGE_POSTER;
    } else {
      this.player = YouTubePlayer('video-player');
      this.player.loadVideoById(videoId);
      this.player.stopVideo();
    }
  }

  async getUserTeams(): Promise<void> {
    this.homeTeamContextReady = false;
    if (this.isBrokerAdmin) {
      this.homeTeamContextReady = true;
      return;
    }
    const userId = this.sessionService.getUserId();
    if (!userId || !this.apiService.isAuthenticated()) {
      this.teams = [];
      this.teamAdminWellspaceCreationEnabled = false;
      return;
    }

    await this.refreshTeamAdminWellspaceCreationCapability(userId);

    const companyState = await this.activeEmployerCompanyService.initialize().catch(() => undefined);
    if (
      companyState
      && this.activeEmployerCompanyService.isSupportedSingleCompanyWorkspaceActive(companyState)
    ) {
      this.isNativeTeamContextActive = true;
      const nativeTeamState = await this.nativeTeamContextService.initialize();
      if (
        this.apiService.isAuthenticated()
        && this.sessionService.getUserId() === userId
      ) {
        this.teams = [...nativeTeamState.teams];
        this.homeTeamContextReady = true;
      }
      return;
    }

    this.isNativeTeamContextActive = false;
    await this.teamsService.getUserTeamsInfo(userId).then(teams => {
      if (!this.apiService.isAuthenticated() || this.sessionService.getUserId() !== userId) {
        return;
      }
      this.teams = teams;
      if (this.teams && this.teams.length > 0) {
        this.teams.forEach(wellspace => wellspace.role = wellspace.ownerUserId === this.sessionService.getUserId() ? '' : 'admin');
      }
      this.homeTeamContextReady = true;
    }).catch(() => {
      if (this.apiService.isAuthenticated() && this.sessionService.getUserId() === userId) {
        this.teams = [];
        this.homeTeamContextReady = true;
      }
    });
  }

  private async refreshTeamAdminWellspaceCreationCapability(userId: string): Promise<void> {
    if (this.sessionService.isTeamAgentSession()) {
      this.teamAdminWellspaceCreationEnabled = true;
      return;
    }

    try {
      const enabled = await this.teamsService.isTeamAdminWellspaceCreationEnabled();
      if (this.apiService.isAuthenticated() && this.sessionService.getUserId() === userId) {
        this.teamAdminWellspaceCreationEnabled = enabled;
      }
    } catch (error) {
      if (this.sessionService.getUserId() === userId) {
        this.teamAdminWellspaceCreationEnabled = false;
      }
      console.error('Unable to resolve Team Admin Wellspace creation access', error);
    }
  }

  showPendingAchMessage(): void {
    this.stipeService.pendingACHCount.subscribe((res: number) => this.showPendingAchMsg = res > 0);
  }

  showIntro(): void {
    const userId = this.sessionService.getUserId();
    if (!this.firstTime || !userId || this.hasAcknowledgedIntro(userId)) {
      return;
    }

    this.firstTime = false;
    this.dialog.open(IntroDialogComponent).afterClosed().subscribe(() => {
      this.localStorageService.setStorage(this.getIntroAcknowledgementKey(userId), 'true');
    });
  }

  private hasAcknowledgedIntro(userId: string): boolean {
    return this.localStorageService.getStorage(this.getIntroAcknowledgementKey(userId)) === 'true';
  }

  private getIntroAcknowledgementKey(userId: string): string {
    return `${HomeComponent.INTRO_ACKNOWLEDGED_STORAGE_KEY_PREFIX}:${userId}`;
  }

  analyticsClicked(): void {
    this.router.navigate(['analytics']);
  }

  manageWellspaceClicked(): void {
    if (this.teamManagerWorkspace?.useNativeNavigation) {
      void this.router.navigate(['/my-team']);
      return;
    }
    if (this.isNativeTeamContextActive) {
      const selectedTeamId = `${this.selectedWellspaceId || this.currentWellspace?.id || ''}`.trim();
      const selectedTeam = this.teams.find((team) => `${team?.id || ''}` === selectedTeamId);
      void this.router.navigate(
        ['/payrolladmin/teams'],
        {
          queryParams: selectedTeam
            ? {teamId: selectedTeam.id, view: 'details'}
            : {teamId: null, view: null},
          queryParamsHandling: 'merge',
        },
      );
      return;
    }
    this.router.navigate(['managewellspace/overview/current']);
  }

  billingClicked(): void {
    this.router.navigate(['billing']);
  }

  settingsClicked(): void {
    this.router.navigate(['profile']);
  }

  manageAccountsClicked(): void {
    this.router.navigate(['settingsandpermissions']);
  }

  selectTeam(team: any): void {
    if (this.teamManagerWorkspace?.useNativeNavigation) {
      void this.teamManagerWorkspace.selectTeam(team?.id).then(selected => {
        if (selected) void this.router.navigate(['/my-team']);
      });
      return;
    }
    if (this.isNativeTeamContextActive) {
      void this.nativeTeamContextService.selectTeam(team?.id);
    } else {
      void this.teamsService.setWellspace(team);
    }
    this.router.navigate(['managewellspace/overview/current']);
  }

  switchWellspace(teamId: string): void {
    const team = this.teams.find(item => item.id === teamId);
    if (!team) {
      return;
    }
    this.selectedWellspaceId = team.id;
    if (this.isNativeTeamContextActive) {
      void this.nativeTeamContextService.selectTeam(team.id);
    } else {
      void this.teamsService.setWellspace(team);
    }
  }

  openTeamsClicked(): void {
    void this.router.navigate(
      ['/payrolladmin/teams'],
      {
        queryParams: {teamId: null, view: null},
        queryParamsHandling: 'merge',
      },
    );
  }

  addTeam(): void {
    const checkAllTeamUserToSeeIfAdmin: boolean = this.teams.some(team => team && team.role && team.role === 'admin');
    if (checkAllTeamUserToSeeIfAdmin) {
      const msg = 'Are you sure you want to proceed? You will become the team owner, ' +
          'responsible for setting up payment methods. You might prefer to ask your team admin to create a new team with you as the delegated admin.';
      this.uiAlertService.presentAlertConfirm(msg).then((confirm: boolean): void => {
        if (confirm) {
          this.createNewWellspace();
        }
      });
    } else {
      this.createNewWellspace();
    }
  }

  createNewWellspace(): void {
    this.utilsService.addWellspace().then((): void => {
      this.getUserTeams();
    }).catch(err => {
      console.error('Error in createNewWellspace', err);
    });
  }

  helpClicked(): void {

  }

}
